'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useWallet } from '@/lib/wallet/WalletContext';

interface WalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WalletModal({ isOpen, onClose }: WalletModalProps) {
  const { connectMetaMask, connectPhantom, connectLedger, isConnecting, error } = useWallet();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleMetaMask = async () => {
    const success = await connectMetaMask();
    if (success) onClose();
  };

  const handlePhantom = async () => {
    const success = await connectPhantom();
    if (success) onClose();
  };

  const handleLedger = async () => {
    await connectLedger();
    if (!error) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-[#111116] border border-[rgba(255,148,54,0.25)] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl"
        style={{ boxShadow: '0 24px 48px rgba(0,0,0,0.6), 0 0 40px rgba(255,148,54,0.1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-[#F1F5F9]">Connect Wallet</h2>
            <p className="text-xs text-[#94A3B8] mt-0.5">Choose your preferred wallet</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[rgba(255,148,54,0.08)] transition-all"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)] text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        {/* Wallet Options */}
        <div className="space-y-3">
          {/* MetaMask */}
          <button
            onClick={handleMetaMask}
            disabled={isConnecting}
            className="w-full flex items-center gap-4 px-4 py-4 rounded-xl bg-[#18181F] border border-[rgba(255,148,54,0.15)] hover:border-[rgba(255,148,54,0.4)] hover:bg-[rgba(255,148,54,0.06)] transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 rounded-xl bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.2)] flex items-center justify-center flex-shrink-0">
              <svg width="22" height="22" viewBox="0 0 35 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M32.958 1L19.198 11.19l2.52-5.965L32.958 1z" fill="#E2761B" stroke="#E2761B" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2.03 1l13.65 10.28-2.4-6.055L2.03 1zM28.17 23.53l-3.665 5.61 7.845 2.16 2.25-7.64-6.43-.13zM.435 23.7l2.235 7.64 7.845-2.16-3.665-5.61-6.415.13z" fill="#E4761B" stroke="#E4761B" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.12 14.41l-2.19 3.31 7.8.345-.275-8.385-5.335 4.73zM24.865 14.41l-5.41-4.82-.18 8.475 7.785-.345-2.195-3.31zM10.515 29.14l4.68-2.275-4.035-3.15-.645 5.425zM19.79 26.865l4.695 2.275-.66-5.425-4.035 3.15z" fill="#E4761B" stroke="#E4761B" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M24.485 29.14l-4.695-2.275.375 3.06-.04 1.26 4.36-2.045zM10.515 29.14l4.36 2.045-.025-1.26.36-3.06-4.695 2.275z" fill="#D7C1B3" stroke="#D7C1B3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14.94 21.97l-3.89-1.145 2.745-1.26 1.145 2.405zM20.045 21.97l1.145-2.405 2.76 1.26-3.905 1.145z" fill="#233447" stroke="#233447" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.515 29.14l.675-5.61-4.34.13 3.665 5.48zM23.81 23.53l.675 5.61 3.665-5.61-4.34-.13zM27.075 17.72l-7.785.345.72 4.005 1.145-2.405 2.76 1.26 3.16-3.205zM11.05 20.825l2.745-1.26 1.13 2.405.735-4.005-7.8-.345 3.19 3.205z" fill="#CD6116" stroke="#CD6116" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.93 17.72l3.27 6.38-.11-3.175-3.16-3.205zM23.91 20.925l-.125 3.175 3.29-6.38-3.165 3.205zM15.675 18.065l-.735 4.005.915 4.72.205-6.215-.385-2.51zM19.29 18.065l-.37 2.495.18 6.23.93-4.72-.74-4.005z" fill="#E4751F" stroke="#E4751F" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.045 21.97l-.93 4.72.665.46 4.035-3.15.125-3.175-3.895 1.145zM11.05 20.825l.11 3.175 4.035 3.15.665-.46-.915-4.72-3.895-1.145z" fill="#F6851B" stroke="#F6851B" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.1 31.185l.04-1.26-.35-.3h-5.18l-.33.3.025 1.26-4.36-2.045 1.525 1.245 3.09 2.145h5.3l3.105-2.145 1.51-1.245-4.375 2.045z" fill="#C0AD9E" stroke="#C0AD9E" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.79 26.865l-.665-.46h-3.83l-.665.46-.36 3.06.33-.3h5.18l.35.3-.34-3.06z" fill="#161616" stroke="#161616" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M33.515 11.565l1.16-5.61L32.958 1l-13.168 9.78 5.065 4.28 7.16 2.09 1.58-1.845-.685-.495 1.09-.995-.84-.65 1.09-.84-.735-.54zM.325 5.955L1.485 11.565l-.75.54 1.09.84-.825.65 1.09.995-.685.495 1.565 1.845 7.16-2.09 5.065-4.28L2.03 1 .325 5.955z" fill="#763D16" stroke="#763D16" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M32.015 17.15l-7.16-2.09 2.195 3.31-3.29 6.38 4.325-.055h6.43l-2.5-7.545zM10.12 15.06l-7.16 2.09-2.485 7.545h6.415l4.31.055-3.27-6.38 2.19-3.31zM19.29 18.065l.455-7.905-2.07-5.6h-4.36l-2.04 5.6.42 7.905.165 2.525.015 6.2h3.83l.015-6.2.17-2.525z" fill="#F6851B" stroke="#F6851B" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-[#F1F5F9] group-hover:text-[#FF9436] transition-colors">MetaMask</p>
              <p className="text-xs text-[#94A3B8]">Connect via browser extension</p>
            </div>
            {isConnecting ? (
              <div className="w-4 h-4 border-2 border-[#FF9436] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="ChevronRightIcon" size={16} className="text-[#94A3B8] group-hover:text-[#FF9436] transition-colors" />
            )}
          </button>

          {/* Phantom */}
          <button
            onClick={handlePhantom}
            disabled={isConnecting}
            className="w-full flex items-center gap-4 px-4 py-4 rounded-xl bg-[#18181F] border border-[rgba(255,148,54,0.15)] hover:border-[rgba(255,148,54,0.4)] hover:bg-[rgba(255,148,54,0.06)] transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 rounded-xl bg-[rgba(171,98,255,0.12)] border border-[rgba(171,98,255,0.2)] flex items-center justify-center flex-shrink-0">
              <svg width="22" height="22" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="128" height="128" rx="64" fill="url(#phantom_grad)"/>
                <defs>
                  <linearGradient id="phantom_grad" x1="0" y1="0" x2="128" y2="128" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#534BB1"/>
                    <stop offset="1" stopColor="#551BF9"/>
                  </linearGradient>
                </defs>
                <path d="M110.584 64.9142H99.142C99.142 41.7651 80.173 23 56.7724 23C33.6612 23 14.8716 41.3057 14.4189 64.0583C13.9489 87.7484 33.5023 107 57.3824 107H61.6825C83.0927 107 110.584 89.0663 110.584 64.9142Z" fill="white"/>
                <path d="M77.5 64.5C77.5 68.9183 73.9183 72.5 69.5 72.5C65.0817 72.5 61.5 68.9183 61.5 64.5C61.5 60.0817 65.0817 56.5 69.5 56.5C73.9183 56.5 77.5 60.0817 77.5 64.5Z" fill="#AB62FF"/>
                <path d="M95.5 64.5C95.5 68.9183 91.9183 72.5 87.5 72.5C83.0817 72.5 79.5 68.9183 79.5 64.5C79.5 60.0817 83.0817 56.5 87.5 56.5C91.9183 56.5 95.5 60.0817 95.5 64.5Z" fill="#AB62FF"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-[#F1F5F9] group-hover:text-[#FF9436] transition-colors">Phantom</p>
              <p className="text-xs text-[#94A3B8]">Connect Solana wallet</p>
            </div>
            {isConnecting ? (
              <div className="w-4 h-4 border-2 border-[#FF9436] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="ChevronRightIcon" size={16} className="text-[#94A3B8] group-hover:text-[#FF9436] transition-colors" />
            )}
          </button>

          {/* Ledger */}
          <button
            onClick={handleLedger}
            disabled={isConnecting}
            className="w-full flex items-center gap-4 px-4 py-4 rounded-xl bg-[#18181F] border border-[rgba(255,148,54,0.15)] hover:border-[rgba(255,148,54,0.4)] hover:bg-[rgba(255,148,54,0.06)] transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 rounded-xl bg-[rgba(0,0,0,0.4)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center flex-shrink-0">
              {/* Ledger logo mark */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="4" fill="#000000"/>
                <path d="M10.2 4H4V14.2H10.2V12.6H5.6V5.6H10.2V4Z" fill="white"/>
                <path d="M10.2 14.2H4V20H14.2V14.2H12.6V18.4H5.6V14.2H10.2Z" fill="white"/>
                <path d="M14.2 4H12.6V10.2H20V8.6H14.2V4Z" fill="white"/>
                <path d="M20 14.2H14.2V20H20V14.2Z" fill="white"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-[#F1F5F9] group-hover:text-[#FF9436] transition-colors">Ledger</p>
              <p className="text-xs text-[#94A3B8]">Connect hardware wallet</p>
            </div>
            {isConnecting ? (
              <div className="w-4 h-4 border-2 border-[#FF9436] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="ChevronRightIcon" size={16} className="text-[#94A3B8] group-hover:text-[#FF9436] transition-colors" />
            )}
          </button>
        </div>

        <p className="text-center text-xs text-[#94A3B8] mt-5">
          By connecting, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
